import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, 
    FileText, 
    Users, 
    Map, 
    Settings,
    Linkedin,
    Mail,
    Megaphone,
    TrendingUp,
    Building,
    FolderOpen,
    LogOut,
    ChevronRight,
    BarChart3
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../Logo';
import { cn } from '../../lib/utils';

const NavItem = ({ to, icon: Icon, label, badge, exact }) => {
    const location = useLocation();
    const isActive = exact ? location.pathname === to : (location.pathname === to || location.pathname.startsWith(`${to}/`));

    return (
        <NavLink
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className={cn(
                'nav-item flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200',
                isActive 
                    ? 'bg-brand-red/10 text-brand-red' 
                    : 'text-content-secondary hover:text-white hover:bg-white/5'
            )}
        >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{label}</span>
            {badge && (
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-brand-red text-white">
                    {badge}
                </span>
            )}
            {isActive && <ChevronRight className="w-4 h-4" />}
        </NavLink>
    );
};

const NavSection = ({ title, children }) => (
    <div className="mb-6">
        <h3 className="px-4 mb-2 text-xs font-bold uppercase tracking-wider text-content-muted">
            {title}
        </h3>
        <nav className="space-y-1">
            {children}
        </nav>
    </div>
);

export const Sidebar = () => {
    const { user, logout, isAdmin } = useAuth();

    const handleLogout = async () => {
        await logout();
        window.location.href = '/login';
    };

    return (
        <aside className="fixed left-0 top-0 h-screen w-64 bg-surface-sidebar flex flex-col z-50">
            {/* Logo */}
            <div className="h-[72px] px-6 flex items-center">
                <Logo />
            </div>

            {/* Navigation */}
            <div className="flex-1 overflow-y-auto py-6 px-3">
                {isAdmin ? (
                    <>
                        <NavSection title="Overview">
                            <NavItem to="/admin" icon={LayoutDashboard} label="Dashboard" exact />
                            <NavItem to="/admin/customers" icon={Users} label="Customers" />
                        </NavSection>
                        <NavSection title="Data Management">
                            <NavItem to="/admin/reports" icon={FileText} label="All Reports" />
                            <NavItem to="/admin/overall-metrics" icon={BarChart3} label="Overall Metrics" />
                        </NavSection>
                    </>
                ) : (
                    <>
                        <NavSection title="Overview">
                            <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
                            <NavItem to="/journey" icon={Map} label="ABM Journey" />
                        </NavSection>
                        <NavSection title="Reports">
                            <NavItem to="/reports/weekly" icon={FileText} label="Weekly Reports" />
                            <NavItem to="/reports/monthly" icon={FileText} label="Monthly Reports" />
                            <NavItem to="/reports/compare" icon={TrendingUp} label="Compare" />
                        </NavSection>
                        <NavSection title="Channels">
                            <NavItem to="/channels/linkedin" icon={Linkedin} label="LinkedIn" />
                            <NavItem to="/channels/email" icon={Mail} label="Email" />
                            <NavItem to="/channels/ads" icon={Megaphone} label="Ads" />
                            <NavItem to="/channels/inbound" icon={TrendingUp} label="Inbound" />
                        </NavSection>
                        <NavSection title="Account">
                            <NavItem to="/customer-details" icon={Building} label="Company Details" />
                            <NavItem to="/documents" icon={FolderOpen} label="Documents" />
                        </NavSection>
                    </>
                )}
            </div>

            {/* User section */}
            <div className="p-4">
                <div className="flex items-center gap-3 px-3 py-2">
                    <div className="w-10 h-10 rounded-full bg-brand-red/20 flex items-center justify-center">
                        <span className="text-brand-red font-bold">
                            {user?.name?.charAt(0) || 'U'}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                        <p className="text-xs text-content-muted truncate">{user?.email}</p>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="mt-2 w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-content-secondary hover:text-white hover:bg-white/5 transition-colors"
                >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
